import type { ViewerShell } from '../../../shell';
import { ACTION_TEXT, jumpTo } from '../actions';
import { conversationStudy, resolveLocation } from '../context';
import { aiLimitText } from '../errors';
import type { Lesion, LesionLocation, LesionMeasurement } from './api';
import { askText } from './ask-text';
import { axes, button, el, resultCard, type ToolDeps } from './dom';
import { changePct, formatChange } from './geometry';
import { trackCandidates } from './lesion-sources';

/**
 * Lesion tracking: a lesion followed from a finding or a measurement, with its size on each study
 * date and the change from the first one, "Buscar en previo" (ris finds it on a prior study in the
 * viewer) and a jump to every measurement. One card in the conversation, rebuilt on every change.
 */

export const LESION_TEXT = {
  button: 'Lesiones',
  title: 'Lesiones en seguimiento',
  loading: 'cargando…',
  empty: 'No hay lesiones en seguimiento para este paciente.',
  loadFailed: 'No fue posible cargar las lesiones en seguimiento.',
  track: 'Seguir lesión…',
  trackTitle: 'Seguir lesión',
  trackLabel: 'Nombre de la lesión',
  trackHint: 'Se guardará con su tamaño actual para compararla en otros estudios.',
  trackConfirm: 'Seguir',
  trackTooShort: 'Escriba al menos 2 caracteres.',
  tracked: 'Lesión en seguimiento.',
  trackFailed: 'No fue posible guardar la lesión.',
  noSources: 'Para seguir una lesión, mídala (Línea o Medir con IA) o use un hallazgo sugerido con recuadro.',
  sources: 'Elija la medición o el hallazgo:',
  jump: 'Ir',
  jumpTitle: 'Ir al corte de esta medición',
  notLoaded: 'Ese estudio no está cargado en el visor.',
  prior: 'Buscar en previo',
  here: 'Buscar en este estudio',
  matching: 'buscando en el previo…',
  matched: (size: string, change: string) => `Encontrada en el previo: ${size} (${change}).`,
  notMatched: 'No se encontró la lesión en el previo.',
  matchFailed: 'No fue posible buscar la lesión en el previo.',
  baseline: 'base',
} as const;

const SOURCE_LABELS: Record<string, string> = { manual: 'manual', assisted: 'asistida', matched: 'encontrada' };
const MIN_LABEL = 2;
const MAX_LABEL = 120;

export interface TrackSource {
  label?: string;
  location: LesionLocation;
  longMm: number | null;
  shortMm: number | null;
}

let card: HTMLElement | null = null;

/**
 * A measurement on the conversation's study is shown in the active cell; one on another study opens
 * beside it (like "Abrir estudio previo"), so the conversation and this list stay on the current study.
 */
export function jumpToMeasurement(shell: ViewerShell, location: LesionLocation, studyUid: string): void {
  if (studyUid === conversationStudy(shell)) return void jumpTo(shell, location);
  const target = resolveLocation(shell, location);
  if (!target) return void shell.toast(ACTION_TEXT.notInViewer, 'warning');
  shell.guard(() => {
    if (shell.viewports().length < 2) shell.setLayout({ rows: 1, columns: 2 });
    const cell = (shell.activeIndex() + 1) % shell.viewports().length;
    void shell.showSeriesIn(cell, target.series, target.index);
  });
}

/** "DD/MM/AAAA" of a study in the viewer, else of the measurement time. */
function dateOf(deps: ToolDeps, m: LesionMeasurement): { key: string; text: string } {
  const study = deps.shell.studies().find((s) => s.studyInstanceUID === m.studyUid);
  const raw = study?.studyDate && /^\d{8}$/.test(study.studyDate) ? study.studyDate : (m.measuredAt ?? '').slice(0, 10).replace(/-/g, '');
  if (!/^\d{8}$/.test(raw)) return { key: m.measuredAt ?? '', text: '—' };
  return { key: raw, text: `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)}` };
}

function measurementRow(deps: ToolDeps, m: LesionMeasurement, baseline: number | null, first: boolean): HTMLElement {
  const row = el('div', 'assistant-lesion-row');
  row.setAttribute('role', 'listitem');
  row.dataset.studyUid = m.studyUid;
  const change = first ? LESION_TEXT.baseline : formatChange(changePct(baseline, m.longMm));
  row.append(
    el('span', 'assistant-lesion-date', dateOf(deps, m).text),
    el('span', 'assistant-lesion-size', axes(m.longMm, m.shortMm)),
    el('span', 'assistant-lesion-change', change),
    el('span', 'assistant-lesion-source', SOURCE_LABELS[m.source] ?? m.source),
  );
  const location = m.location;
  const jump = button(LESION_TEXT.jump, 'assistant-lesion-jump', () => {
    if (location) jumpToMeasurement(deps.shell, location, m.studyUid);
  }, location ? LESION_TEXT.jumpTitle : LESION_TEXT.notLoaded);
  jump.disabled = !location;
  row.appendChild(jump);
  return row;
}

async function matchPrior(deps: ToolDeps, lesion: Lesion, priorUid: string, trigger: HTMLButtonElement): Promise<void> {
  trigger.disabled = true;
  trigger.textContent = LESION_TEXT.matching;
  try {
    const out = await deps.api.matchPrior(lesion.id, priorUid);
    if (out.measurement) deps.shell.toast(LESION_TEXT.matched(axes(out.measurement.longMm, out.measurement.shortMm), formatChange(out.pct)));
    else deps.shell.toast(LESION_TEXT.notMatched, 'warning');
  } catch (error) {
    console.warn('[viewer] lesion match failed', error);
    deps.shell.toast(aiLimitText(error) ?? LESION_TEXT.matchFailed, 'error');
  }
  await showLesions(deps);
}

function priorButtons(deps: ToolDeps, lesion: Lesion): HTMLElement[] {
  const measured = new Set(lesion.measurements.map((m) => m.studyUid));
  if (measured.size === 0) return [];
  const priors = deps.shell.studies().filter((s) => !measured.has(s.studyInstanceUID));
  const current = conversationStudy(deps.shell);
  return priors.map((study) => {
    const date = dateOf(deps, { id: '', studyUid: study.studyInstanceUID, location: null, longMm: null, shortMm: null, source: '', measuredAt: null }).text;
    // A lesion followed from an earlier study is looked for on the study open in the conversation too.
    const base = study.studyInstanceUID === current ? LESION_TEXT.here : LESION_TEXT.prior;
    const label = priors.length > 1 ? `${base} (${date})` : base;
    const b: HTMLButtonElement = button(label, 'assistant-lesion-prior', () => void matchPrior(deps, lesion, study.studyInstanceUID, b));
    b.dataset.priorUid = study.studyInstanceUID;
    return b;
  });
}

function renderLesion(deps: ToolDeps, lesion: Lesion): HTMLElement {
  const item = el('div', 'assistant-lesion');
  item.dataset.lesionId = lesion.id;
  item.appendChild(el('div', 'assistant-lesion-label', lesion.label));
  const rows = el('div', 'assistant-lesion-rows');
  rows.setAttribute('role', 'list');
  const ordered = [...lesion.measurements].sort((a, b) => dateOf(deps, a).key.localeCompare(dateOf(deps, b).key));
  const baseline = ordered.find((m) => m.longMm !== null)?.longMm ?? null;
  ordered.forEach((m, i) => rows.appendChild(measurementRow(deps, m, baseline, i === 0)));
  item.appendChild(rows);
  const priors = priorButtons(deps, lesion);
  if (priors.length > 0) {
    const row = el('div', 'assistant-finding-actions');
    row.append(...priors);
    item.appendChild(row);
  }
  return item;
}

function sourcePicker(deps: ToolDeps, host: HTMLElement): void {
  const sources = trackCandidates(deps.shell, conversationStudy(deps.shell));
  host.replaceChildren();
  if (sources.length === 0) return void host.appendChild(el('p', 'assistant-tool-note', LESION_TEXT.noSources));
  host.appendChild(el('p', 'assistant-tool-note', LESION_TEXT.sources));
  const list = el('div', 'assistant-lesion-sources');
  for (const source of sources) {
    list.appendChild(button(source.title, 'assistant-lesion-source-pick', () => void trackLesion(deps, source)));
  }
  host.appendChild(list);
}

function fill(deps: ToolDeps, body: HTMLElement, lesions: Lesion[]): void {
  const picker = el('div', 'assistant-lesion-picker');
  const head = el('div', 'assistant-finding-actions');
  head.appendChild(button(LESION_TEXT.track, 'assistant-lesion-track', () => sourcePicker(deps, picker)));
  body.replaceChildren(head, picker);
  if (lesions.length === 0) return void body.appendChild(el('p', 'assistant-tool-note assistant-lesion-empty', LESION_TEXT.empty));
  const list = el('div', 'assistant-lesions');
  lesions.forEach((lesion) => list.appendChild(renderLesion(deps, lesion)));
  body.appendChild(list);
}

/** Shows (or rebuilds, at the end of the conversation) the tracked-lesions card. */
export async function showLesions(deps: ToolDeps): Promise<void> {
  const { card: next, body } = resultCard('lesions', LESION_TEXT.title);
  body.appendChild(el('div', 'assistant-thinking', LESION_TEXT.loading));
  if (card) deps.panel.removeResult(card);
  card = next;
  const study = conversationStudy(deps.shell);
  deps.panel.appendResult(next, study);
  let lesions: Lesion[];
  try {
    lesions = await deps.api.lesions(study);
  } catch (error) {
    console.warn('[viewer] lesion list failed', error);
    body.replaceChildren(el('p', 'assistant-tool-note', LESION_TEXT.loadFailed));
    return;
  }
  if (card === next) fill(deps, body, lesions);
}

/** Starts following a lesion from a measurement or a finding: asks its name, stores it, shows the list. */
export async function trackLesion(deps: ToolDeps, source: TrackSource): Promise<void> {
  const label = await askText(deps.shell, {
    title: LESION_TEXT.trackTitle, label: LESION_TEXT.trackLabel, hint: LESION_TEXT.trackHint,
    className: 'assistant-lesion-dialog', confirm: LESION_TEXT.trackConfirm, initial: source.label ?? '',
    minLength: MIN_LABEL, maxLength: MAX_LABEL, tooShort: LESION_TEXT.trackTooShort,
  });
  if (label === null) return;
  const body: Parameters<ToolDeps['api']['createLesion']>[1] = { label, location: source.location };
  if (source.longMm !== null) body.long_mm = source.longMm;
  if (source.shortMm !== null) body.short_mm = source.shortMm;
  try {
    await deps.api.createLesion(conversationStudy(deps.shell), body);
    deps.shell.toast(LESION_TEXT.tracked);
  } catch (error) {
    console.warn('[viewer] lesion create failed', error);
    deps.shell.toast(aiLimitText(error) ?? LESION_TEXT.trackFailed, 'error');
    return;
  }
  await showLesions(deps);
}
